import { computed } from 'vue';
import { useCreateFieldGetter } from '@/common/customisation/composables/useCreateFieldGetter';
import { PUI } from '@pleased-npm/fe-customisation';

const otpConfirmationBlockParams = {
  registryId: PUI.REGISTRY_IDS.GUEST_FORM_PAGE,
  blockKey: PUI.GUEST_FORM_BLOCK_KEYS.OTP_CONFIRMATION,
} as const;

/**
 * Composable for OTP title customisation
 */
export const useOTPTitle = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.OTP_TITLE,
  });

  const titleText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const titleTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    titleText,
    titleTextColor,
  };
};

/**
 * Dismissible error banner in the OTP title row (message from app).
 */
export const useOTPErrorBanner = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.OTP_ERROR_BANNER,
  });

  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const dismissActionColor = getField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR);

  return {
    isVisible,
    backgroundColor,
    borderColor,
    textColor,
    dismissActionColor,
  };
};

/**
 * Composable for OTP instruction text customisation
 */
export const useOTPText = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.OTP_TEXT,
  });

  const text = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    text,
    textColor,
  };
};

/**
 * Composable for OTP input field customisation
 */
export const useOTPInput = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.OTP_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    placeholderColor,
    textColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Composable for OTP error message customisation
 */
export const useOTPError = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.OTP_ERROR,
  });

  const errorText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const errorTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    errorText,
    errorTextColor,
  };
};

/**
 * Composable for OTP resend button customisation
 */
export const useOTPResendButton = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.RESEND_BUTTON,
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const disabledButtonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR);
  const disabledButtonBorderColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledButtonTextColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  return {
    buttonText,
    buttonBackgroundColor,
    buttonBorderColor,
    buttonTextColor,
    disabledButtonBackgroundColor,
    disabledButtonBorderColor,
    disabledButtonTextColor,
  };
};

/**
 * Composable for OTP resending indicator text customisation
 */
export const useOTPResendingText = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.RESENDING_TEXT,
  });

  const text = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    text,
    textColor,
  };
};

/**
 * Composable for OTP resend timer text customisation
 * Note: Use {seconds} placeholder in the text for the countdown
 */
export const useOTPResendTimer = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.RESEND_TIMER,
  });

  const timerText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const timerTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    timerText,
    timerTextColor,
  };
};

/**
 * Composable for OTP change email button customisation
 */
export const useOTPChangeEmailButton = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.CHANGE_EMAIL_BUTTON,
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);

  return {
    buttonText,
    buttonBackgroundColor,
    buttonTextColor,
    buttonBorderColor,
  };
};

/**
 * Cancel / cancel-support on OTP step — `otp-confirmation` block.
 * Uses section `cancel-button` (not on `PUI.GUEST_FORM_SECTION_KEYS` in current fe-customisation typings).
 */
export const useOTPCancelButton = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: 'cancel-button',
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const disabledButtonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR);
  const disabledButtonBorderColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledButtonTextColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  return {
    isVisible,
    buttonText,
    buttonBackgroundColor,
    buttonBorderColor,
    buttonTextColor,
    disabledButtonBackgroundColor,
    disabledButtonBorderColor,
    disabledButtonTextColor,
  };
};

/**
 * Whether guest OTP email verification is enabled. Absent/empty ⇒ enabled
 * (preserves legacy behaviour); only an explicit "false" disables it.
 */
export const useOTPVerification = () => {
  const { getField } = useCreateFieldGetter({
    ...otpConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.OTP_VERIFICATION,
  });

  const otpEnabledField = getField(PUI.COMMON_FIELD_KEYS.OTP_ENABLED);
  const otpEnabled = computed(() => otpEnabledField.value !== 'false');

  return { otpEnabled };
};

/**
 * Combined composable that returns all OTP customisations
 */
export const useOTPAllCustomisation = () => {
  const title = useOTPTitle();
  const errorBanner = useOTPErrorBanner();
  const text = useOTPText();
  const input = useOTPInput();
  const error = useOTPError();
  const resendButton = useOTPResendButton();
  const resendingText = useOTPResendingText();
  const resendTimer = useOTPResendTimer();
  const changeEmailButton = useOTPChangeEmailButton();
  const cancelButton = useOTPCancelButton();

  return {
    title,
    errorBanner,
    text,
    input,
    error,
    resendButton,
    resendingText,
    resendTimer,
    changeEmailButton,
    cancelButton,
  };
};
